import { Autocomplete, TextField } from '@mui/material'
import { useState, type Ref } from 'react'

import { IwiData } from '@/models/Iwi'

function getAsciiName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
}

interface IwiPickerProps {
  iwiList: IwiData[] | undefined
  iwiLoading?: boolean
  selectedIds: number[]
  onChange: (ids: number[]) => void
  ariaLabel?: string
  /** Marks the input invalid; pair with errorId so the message is read with it. */
  error?: boolean
  errorId?: string
  inputRef?: Ref<HTMLInputElement>
}

/** Type-ahead iwi picker. Shows nothing until the user types; macron-insensitive. */
export default function IwiPicker({
  iwiList,
  iwiLoading = false,
  selectedIds,
  onChange,
  ariaLabel,
  error = false,
  errorId,
  inputRef,
}: IwiPickerProps) {
  const [inputValue, setInputValue] = useState('')

  return (
    <Autocomplete
      multiple
      value={
        Array.isArray(iwiList)
          ? selectedIds
              .map((id) => iwiList.find((iwi: IwiData) => iwi.id === id))
              .filter((iwi): iwi is IwiData => Boolean(iwi))
          : []
      }
      sx={{ mb: 1 }}
      slotProps={{
        chip: {
          role: undefined, // remove misleading button role for accessibility
        },
      }}
      onChange={(event, newValues) => {
        onChange(
          newValues
            .filter((value): value is IwiData => typeof value !== 'string')
            .map((value) => value.id),
        )
      }}
      options={iwiList ?? []}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.name
      }
      filterOptions={(options, params) => {
        const typed = params.inputValue.toLowerCase().trim()
        if (!typed) return []
        const asciiInput = getAsciiName(typed)

        return options.filter((option) => {
          const asciiName = (option as IwiData & { ascii_name?: string })
            .ascii_name
          if (asciiName) return asciiName.toLowerCase().includes(asciiInput)
          return getAsciiName(option.name).includes(asciiInput)
        })
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.name}
        </li>
      )}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      freeSolo
      noOptionsText={
        iwiLoading
          ? 'Loading iwi...'
          : !inputValue
            ? 'Start typing to search iwi...'
            : 'No matching iwi found.'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          inputRef={inputRef}
          placeholder="Type to search for iwi..."
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#757575',
            },
          }}
          InputProps={{
            ...params.InputProps,
            style: { backgroundColor: '#fff' },
          }}
          inputProps={{
            ...params.inputProps,
            ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
            ...(error ? { 'aria-invalid': true } : {}),
            ...(errorId ? { 'aria-describedby': errorId } : {}),
          }}
        />
      )}
    />
  )
}
